<template>
  <div class="np-form">
    <div class="np-header">
      <div class="np-header-left">
        <span class="np-icon"><font-awesome-icon :icon="['fas', 'building']" /></span>
        <span class="np-title">New Presale Project</span>
      </div>
      <button class="np-close" type="button" :disabled="saving" aria-label="Close" @click="$emit('close')">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
    </div>

    <!-- Done. The panel stays up rather than jumping to the project, so the
         warnings a save came back with are read before the page changes. -->
    <div v-if="created" class="np-body">
      <div class="np-alert np-alert--ok">
        <font-awesome-icon :icon="['fas', 'check-circle']" />
        <div>
          <div class="np-alert-title">{{ created.title }} created</div>
          <div>
            Project {{ created.nid }} — with its first building ({{ created.fid }})
            and first component ({{ created.uid }}).
          </div>
        </div>
      </div>

      <div v-if="created.warnings && created.warnings.length" class="np-alert np-alert--warn">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <div>
          <div class="np-alert-title">Saved, with notes</div>
          <ul class="np-warn-list">
            <li v-for="(warning, i) in created.warnings" :key="i">{{ warning }}</li>
          </ul>
        </div>
      </div>

      <p class="np-help">
        Permit applications, unit mix and floor plans are added on the project
        page — this form only opens the record.
      </p>
    </div>

    <div v-else class="np-body">
      <p class="np-hint">
        Creates the project record together with its first building and first
        component, so it opens straight away. Everything here can be edited
        afterwards; only the name is needed to start.
      </p>

      <!-- The project -->
      <div class="np-row">
        <label class="np-label" for="np-title">Project name <span class="np-required">*</span></label>
        <input id="np-title" v-model="form.title" class="np-input" type="text" :disabled="saving"
          placeholder="e.g. Marine Landing" @keyup.enter="save" />
      </div>

      <div class="np-row">
        <label class="np-label" for="np-address">Address</label>
        <input id="np-address" v-model="form.field_address" class="np-input" type="text" :disabled="saving"
          placeholder="e.g. 8188 Manitoba St" />
        <p class="np-help">
          Carried down to the building and the component unless they are given
          their own on the project page.
        </p>
      </div>

      <!-- City / neighbourhood. A type-ahead rather than a select: the
           vocabulary runs to thousands of terms, and the same neighbourhood
           name repeats across cities — so the path is what is matched and
           shown, not the bare label. -->
      <div class="np-row">
        <label class="np-label" for="np-city">City / neighbourhood</label>
        <div class="np-city">
          <input id="np-city" v-model="cityQuery" class="np-input" type="text" autocomplete="off"
            :disabled="saving" placeholder="Start typing a city…" @focus="cityOpen = true"
            @blur="cityOpen = false" @input="onCityInput" />
          <button v-if="cityTid" type="button" class="np-city-clear" :disabled="saving"
            title="Clear city" aria-label="Clear city" @click="clearCity">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
        <div v-if="cityOpen && cityMatches.length" class="np-picker">
          <!-- mousedown, not click: the input's blur closes the picker, and it
               fires first — the pick would land on an element already gone. -->
          <button v-for="option in cityMatches" :key="option.tid" type="button" class="np-picker-item"
            @mousedown.prevent="pickCity(option)">
            <span class="np-picker-label">{{ option.label }}</span>
            <span class="np-picker-meta">{{ option.path }}</span>
          </button>
          <div v-if="cityMore" class="np-picker-more">Keep typing to narrow the list…</div>
        </div>
        <p v-if="cityQuery && !cityTid" class="np-help np-help--warn">
          Pick one from the list — a typed name that is not chosen is not saved.
        </p>
      </div>

      <!-- Optional sections. Collapsed by default: a project is routinely
           opened from a name and an address alone, and the rest is filled in on
           the project page once it exists. -->
      <div v-for="section in sections" :key="section.key" class="np-section">
        <button type="button" class="np-section-head" :aria-expanded="String(open[section.key])"
          @click="open[section.key] = !open[section.key]">
          <span>
            <span class="np-section-title">{{ section.title }}</span>
            <span class="np-section-note">{{ section.note }}</span>
          </span>
          <font-awesome-icon :icon="['fas', open[section.key] ? 'chevron-up' : 'chevron-down']" />
        </button>

        <div v-if="open[section.key]" class="np-section-body">
          <div class="np-row--pair">
            <div v-for="field in section.fields" :key="field.key">
              <label class="np-label" :for="'np-' + section.key + '-' + field.key">{{ field.label }}</label>
              <select v-if="field.options" :id="'np-' + section.key + '-' + field.key" class="np-input"
                :disabled="saving || loadingOptions" v-model="form[section.key][field.key]">
                <option value="">{{ loadingOptions ? "Loading…" : "—" }}</option>
                <option v-for="option in options[field.options]" :key="option.id" :value="option.id">
                  {{ option.label }}
                </option>
              </select>
              <!-- `step` is set only on the decimal fields; undefined leaves
                   the attribute off, and a number input without it rejects
                   anything that is not a whole number. -->
              <input v-else :id="'np-' + section.key + '-' + field.key" class="np-input"
                :type="field.type || 'text'" :step="field.step" :placeholder="field.placeholder || ''"
                :disabled="saving" v-model="form[section.key][field.key]" />
            </div>
          </div>
          <p v-if="section.help" class="np-help">{{ section.help }}</p>
        </div>
      </div>

      <!-- A refused duplicate. The project it collided with is offered rather
           than named, because opening it is nearly always what was meant. -->
      <div v-if="duplicate" class="np-alert np-alert--warn">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <div>
          <div class="np-alert-title">This may already exist</div>
          <div>{{ duplicate.message }}</div>
          <div class="np-alert-actions">
            <button type="button" class="np-link-btn" @click="$emit('open-project', duplicate.nid)">
              Open {{ duplicate.title || `project ${duplicate.nid}` }}
            </button>
            <button type="button" class="np-link-btn" :disabled="saving" @click="save(true)">
              Create it anyway
            </button>
          </div>
        </div>
      </div>

      <div v-if="error" class="np-alert np-alert--error">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        <div>
          <div class="np-alert-title">Could not create the project</div>
          <div>{{ error }}</div>
        </div>
      </div>
    </div>

    <div class="np-footer">
      <div class="np-footer-status">
        <span v-if="saving">Saving…</span>
        <span v-else-if="created">Created by you just now.</span>
        <span v-else-if="!canSave">A project name is required.</span>
      </div>
      <div class="np-footer-actions">
        <template v-if="created">
          <button type="button" class="np-cancel-btn" @click="reset">Create another</button>
          <button type="button" class="np-save-btn" @click="$emit('open-project', created.nid)">
            Open project
          </button>
        </template>
        <template v-else>
          <button type="button" class="np-cancel-btn" :disabled="saving" @click="$emit('close')">Cancel</button>
          <button type="button" class="np-save-btn" :disabled="!canSave || saving" @click="save(false)">
            <font-awesome-icon v-if="saving" :icon="['fas', 'spinner']" class="fa-spin" />
            {{ saving ? "Creating…" : "Create project" }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { useProjectStore } from "../../store/ProjectStore";
import { cityOptionList } from "../../composables/cities";
import {
  PRESALE_FIELDS,
  compactFields,
  emptyFields,
} from "../../data/projectCollectionFields";

// How many city matches the picker shows at once. The list runs to thousands of
// terms; past a dozen the reader is scrolling rather than reading, and the right
// answer is a longer query.
const CITY_LIMIT = 12;

// The optional section. NewPresaleForm writes the project node and nothing
// else: the building and the component the server always creates alongside it
// take the project's name, address and city and are filled in afterwards, on
// the Unit & Building tab — or through ProjectCollectionForm, which is what
// adds a further building or component and keeps its own field lists.
//
// Still an array of sections rather than one flat list: the collapse, the
// per-section reset and the payload key all read off it, so putting a second
// level back is an entry here and nothing else.
const SECTIONS = [
  {
    key: "presale",
    title: "Project details",
    note: "Land, lot and cost figures on the project itself",
    help:
      "All optional — anything left blank is not written, and every one of these is editable on the project page afterwards.",
    fields: PRESALE_FIELDS,
  },
];

const emptySections = () =>
  SECTIONS.reduce((state, section) => {
    state[section.key] = emptyFields(section.fields);
    return state;
  }, {});

export default {
  name: "NewPresaleForm",
  data() {
    return {
      form: { title: "", field_address: "", ...emptySections() },
      // The picked term, kept apart from the text in the box: a name typed and
      // left unpicked must not be saved as a city.
      cityTid: null,
      cityQuery: "",
      cityOpen: false,
      open: { presale: false },
      options: {
        generalUnitTypes: [],
        projectTypes: [],
        statuses: [],
      },
      loadingOptions: false,
      saving: false,
      error: null,
      // The backend's duplicate refusal, carrying the project it collided with.
      duplicate: null,
      // { nid, fid, uid, title, warnings } once a save has gone through.
      created: null,
    };
  },
  computed: {
    projectStore() {
      return useProjectStore();
    },
    sections() {
      return SECTIONS;
    },
    canSave() {
      return Boolean(this.form.title.trim());
    },
    // Matched against the whole path, so "burnaby edmonds" and "edmonds" both
    // find the same term. Two characters before anything shows: one letter
    // matches most of the vocabulary and reads as a broken list.
    cityHits() {
      const query = this.cityQuery.trim().toLowerCase();
      if (query.length < 2) return [];
      return this.cityList.filter((option) => option.path.toLowerCase().includes(query));
    },
    cityMatches() {
      return this.cityHits.slice(0, CITY_LIMIT);
    },
    cityMore() {
      return this.cityHits.length > CITY_LIMIT;
    },
  },
  created() {
    // Role-filtered at call time, so it is read once per open rather than at
    // module load — see cityOptionList().
    this.cityList = cityOptionList();
    // No field declares `options` while none of the three levels offers a term
    // reference, and fetching the vocabularies anyway is three requests nothing
    // reads. Asked of the definitions rather than hard-coded, so restoring a
    // term field turns the fetch back on by itself.
    if (SECTIONS.some((section) => section.fields.some((field) => field.options))) {
      this.loadOptions();
    }
  },
  methods: {
    async loadOptions() {
      this.loadingOptions = true;
      try {
        this.options = await this.projectStore.fetchProjectTermOptions();
      } catch (err) {
        console.error("NewPresaleForm loadOptions error:", err);
      } finally {
        this.loadingOptions = false;
      }
    },

    onCityInput() {
      this.cityOpen = true;
      // Editing the text abandons the pick it no longer matches.
      this.cityTid = null;
    },

    pickCity(option) {
      this.cityTid = option.tid;
      this.cityQuery = option.path;
      this.cityOpen = false;
    },

    clearCity() {
      this.cityTid = null;
      this.cityQuery = "";
      this.cityOpen = false;
    },

    buildPayload(allowDuplicate) {
      const payload = { title: this.form.title.trim() };
      const address = this.form.field_address.trim();
      if (address) payload.field_address = address;
      if (this.cityTid) payload.field_city_neighbourhood = this.cityTid;

      SECTIONS.forEach((section) => {
        const fields = compactFields(this.form[section.key]);
        // An untouched section is left off the payload entirely rather than
        // sent empty — the endpoint reads a missing level as "no fields of my
        // own", which is exactly what that is.
        if (Object.keys(fields).length) payload[section.key] = fields;
      });

      if (allowDuplicate) payload.allow_duplicate = true;
      return payload;
    },

    async save(allowDuplicate) {
      if (!this.canSave || this.saving) return;
      this.saving = true;
      this.error = null;
      this.duplicate = null;
      try {
        const data = await this.projectStore.createPresaleProject(
          this.buildPayload(allowDuplicate === true)
        );
        // A duplicate comes back as data rather than an error — the store
        // documents why. Nothing was written.
        if (data.status !== "ok") {
          this.duplicate = data;
          return;
        }
        this.created = data;
        this.$emit("created", data);
      } catch (err) {
        console.error("NewPresaleForm save error:", err);
        this.error = err.message || "Could not create the project.";
      } finally {
        this.saving = false;
      }
    },

    reset() {
      this.form = { title: "", field_address: "", ...emptySections() };
      this.clearCity();
      this.open = { presale: false };
      this.error = null;
      this.duplicate = null;
      this.created = null;
    },
  },
};
</script>

<style scoped>
.np-form {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  color: var(--rpd-ink);
  font-family: var(--rpd-font-sans);
}

.np-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--rpd-line);
}

.np-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.np-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-bg-2);
  color: var(--rpd-navy-600);
}

.np-title {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.np-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: var(--rpd-r-sm);
  background: transparent;
  color: var(--rpd-muted);
  cursor: pointer;
}

.np-close:hover:not(:disabled) {
  background: var(--rpd-bg-2);
  color: var(--rpd-ink);
}

.np-body {
  padding: 16px 20px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}

.np-hint,
.np-help {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rpd-muted);
}

.np-help--warn {
  color: var(--rpd-coral-dark);
}

.np-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.np-row--pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.np-row--pair > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.np-label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
}

.np-required {
  color: var(--rpd-coral-dark);
}

.np-input {
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-ink);
  font-size: 13px;
  font-family: inherit;
}

.np-input:focus {
  outline: none;
  border-color: var(--rpd-navy-400);
}

.np-input:disabled {
  background: var(--rpd-bg-2);
  color: var(--rpd-muted);
  cursor: not-allowed;
}

/* City type-ahead */
.np-city {
  position: relative;
  display: flex;
}

.np-city .np-input {
  padding-right: 32px;
}

.np-city-clear {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: var(--rpd-r-sm);
  background: transparent;
  color: var(--rpd-muted);
  font-size: 11px;
  cursor: pointer;
}

.np-city-clear:hover:not(:disabled) {
  background: var(--rpd-bg-2);
  color: var(--rpd-ink);
}

.np-picker {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-md);
  background: var(--rpd-white);
}

.np-picker-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-bottom: 1px solid var(--rpd-line);
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.np-picker-item:last-child {
  border-bottom: none;
}

.np-picker-item:hover {
  background: var(--rpd-bg-2);
}

.np-picker-label {
  font-size: 13px;
  font-weight: 600;
}

.np-picker-meta,
.np-picker-more {
  font-size: 11px;
  color: var(--rpd-muted);
}

.np-picker-more {
  padding: 7px 10px;
  border-top: 1px solid var(--rpd-line);
}

/* Collapsible optional sections */
.np-section {
  border: 1px solid var(--rpd-line-2);
  border-radius: var(--rpd-r-md);
  background: var(--rpd-bg);
}

.np-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: var(--rpd-r-md);
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.np-section-title {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

.np-section-note {
  display: block;
  font-size: 11px;
  color: var(--rpd-muted);
}

.np-section-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 14px 14px;
}

.np-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--rpd-r-md);
  font-size: 12px;
  line-height: 1.5;
}

.np-alert-title {
  font-weight: 700;
}

.np-alert-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
}

.np-link-btn {
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.np-link-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.np-warn-list {
  margin: 4px 0 0;
  padding-left: 16px;
}

.np-alert--error {
  border: 1px solid var(--rpd-coral);
  background: rgba(242, 106, 111, 0.1);
  color: var(--rpd-coral-dark);
}

.np-alert--warn {
  border: 1px solid var(--rpd-amber);
  background: rgba(244, 183, 64, 0.12);
  color: var(--rpd-ink-2);
}

.np-alert--ok {
  border: 1px solid var(--rpd-line);
  background: var(--rpd-bg-2);
  color: var(--rpd-ink-2);
}

.np-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid var(--rpd-line);
  background: var(--rpd-bg);
}

.np-footer-status {
  font-size: 12px;
  color: var(--rpd-muted);
}

.np-footer-actions {
  display: flex;
  gap: 8px;
}

.np-cancel-btn,
.np-save-btn {
  padding: 8px 14px;
  border-radius: var(--rpd-r-sm);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
}

.np-cancel-btn {
  border: 1px solid var(--rpd-line);
  background: var(--rpd-white);
  color: var(--rpd-ink-2);
}

.np-cancel-btn:hover:not(:disabled) {
  background: var(--rpd-bg-2);
}

.np-save-btn {
  border: 1px solid var(--rpd-navy-700);
  background: var(--rpd-navy-700);
  color: var(--rpd-white);
}

.np-save-btn:hover:not(:disabled) {
  background: var(--rpd-navy-800);
}

.np-cancel-btn:disabled,
.np-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Narrow screens: paired fields and the footer stack rather than squeezing,
   and the actions go full width for thumb reach. */
@media (max-width: 575px) {
  .np-row--pair {
    grid-template-columns: 1fr;
  }

  .np-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .np-footer-actions {
    flex-direction: column-reverse;
  }
}

html.dark .np-form {
  color: #e5e7eb;
}

html.dark .np-header,
html.dark .np-footer {
  border-color: #2b3441;
}

html.dark .np-footer {
  background: #161c25;
}

html.dark .np-icon,
html.dark .np-close:hover:not(:disabled) {
  background: #232c38;
  color: #e5e7eb;
}

html.dark .np-hint,
html.dark .np-help,
html.dark .np-footer-status,
html.dark .np-close,
html.dark .np-picker-meta,
html.dark .np-picker-more,
html.dark .np-section-note {
  color: #9aa4b2;
}

html.dark .np-input {
  background: #1b222c;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .np-input:focus {
  border-color: var(--rpd-navy-300);
}

html.dark .np-input:disabled {
  background: #232c38;
  color: #9aa4b2;
}

html.dark .np-picker,
html.dark .np-section {
  background: #1b222c;
  border-color: #2b3441;
}

html.dark .np-picker-item {
  border-color: #2b3441;
}

html.dark .np-picker-item:hover,
html.dark .np-city-clear:hover:not(:disabled) {
  background: #232c38;
  color: #e5e7eb;
}

html.dark .np-cancel-btn {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .np-cancel-btn:hover:not(:disabled) {
  background: #2b3441;
}

html.dark .np-alert--warn {
  color: #f2d9a3;
}

html.dark .np-alert--ok {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}
</style>
